import { IndesignRoute, getIndesignServiceUrl } from 'lib/constants';
import { IndesignServerClient } from 'lib/indesign/request';
import {
  CallIndesignArgs,
  CallIndesignResponse,
  callInDesignService
} from 'lib/requests';
import { EDisplayParams, TemplateSpecs } from 'lib/types';
import { LaunchDarklyFlags } from 'lib/types/launchDarklyFlags';
import { logAndCaptureMessage } from 'utils';
import { ColumnService } from 'lib/services/directory';
import Firebase from 'EnoticeFirebase';
import { getBooleanFlag, setCustomLDContext } from './flags';

const getUserInfo = () => {
  try {
    const userAuth = Firebase.auth().currentUser;
    return {
      email: userAuth?.email || 'unknown',
      uid: userAuth?.uid || 'unknown'
    };
  } catch {
    return {
      email: 'unknown',
      uid: 'unknown'
    };
  }
};

/**
 * Check if an error is a network failure (e.g., "Failed to fetch", axios network errors)
 * Network failures should be retried, but HTTP errors (4xx, 5xx) should not
 */
const isNetworkError = (error: Error | unknown): boolean => {
  if (!(error instanceof Error)) {
    return false;
  }

  const errorMessage = error.message.toLowerCase();

  // Check for common network error messages
  if (
    errorMessage.includes('failed to fetch') ||
    errorMessage.includes('networkerror') ||
    errorMessage.includes('network request failed') ||
    errorMessage.includes('load failed') ||
    errorMessage.includes('econnaborted') ||
    errorMessage.includes('enotfound') ||
    errorMessage.includes('econnrefused') ||
    errorMessage.includes('etimedout') ||
    errorMessage.includes('err_network')
  ) {
    return true;
  }

  // Check the cause property (axios errors are wrapped with cause)
  const { cause } = error as any;
  if (cause) {
    // Check if cause is an axios error with network-related codes
    const causeCode = (cause as any).code?.toLowerCase();
    if (
      causeCode === 'econnaborted' ||
      causeCode === 'enotfound' ||
      causeCode === 'econnrefused' ||
      causeCode === 'etimedout' ||
      causeCode === 'err_network' ||
      causeCode === 'econnreset'
    ) {
      return true;
    }

    // Check cause message
    if (cause instanceof Error) {
      const causeMessage = cause.message.toLowerCase();
      if (
        causeMessage.includes('network') ||
        causeMessage.includes('timeout') ||
        causeMessage.includes('econn')
      ) {
        return true;
      }
    }
  }

  // Check for axios error structure
  const axiosError = error as any;
  if (axiosError.code) {
    const code = axiosError.code.toLowerCase();
    if (
      code === 'econnaborted' ||
      code === 'enotfound' ||
      code === 'econnrefused' ||
      code === 'etimedout' ||
      code === 'err_network' ||
      code === 'econnreset'
    ) {
      return true;
    }
  }

  return false;
};

const logIndesignFallback = (route: string, error: Error) => {
  const userInfo = getUserInfo();
  logAndCaptureMessage(
    `InDesign service call failed with Cloud Run URL, falling back to old API URL`,
    {
      service: ColumnService.INDESIGN,
      route,
      userEmail: userInfo.email,
      userId: userInfo.uid,
      error: error.message,
      fallbackReason: 'cloud_run_api_failed',
      networkError: 'true'
    }
  );
};

export const getIndesignServerClient =
  async (): Promise<IndesignServerClient> => {
    await setCustomLDContext({
      route: 'indesign'
    });

    const useCloudRunApi = getBooleanFlag(
      LaunchDarklyFlags.USE_CLOUD_RUN_API,
      false
    );

    return {
      async call(route, args) {
        try {
          return await callInDesignService(
            `${getIndesignServiceUrl(useCloudRunApi)}/${route}`,
            args.request,
            useCloudRunApi,
            args.options
          );
        } catch (error) {
          // Only retry network failures (e.g., "Failed to fetch"), not HTTP errors
          if (useCloudRunApi && isNetworkError(error)) {
            logIndesignFallback(route, error as Error);
            return await callInDesignService(
              `${getIndesignServiceUrl(false)}/${route}`,
              args.request,
              false,
              args.options
            );
          }
          throw error;
        }
      }
    };
  };

export const callIndesignTemplateSpecs = async (
  args: CallIndesignArgs,
  isRetry = false
): Promise<CallIndesignResponse<TemplateSpecs>> => {
  await setCustomLDContext({
    route: 'indesign'
  });

  const useCloudRunApi = isRetry
    ? false
    : getBooleanFlag(LaunchDarklyFlags.USE_CLOUD_RUN_API, false);
  const route = IndesignRoute.template_specs;

  try {
    return await callInDesignService(
      `${getIndesignServiceUrl(useCloudRunApi)}/${route}`,
      args.request,
      useCloudRunApi,
      args.options
    );
  } catch (error) {
    // Only retry network failures (e.g., "Failed to fetch"), not HTTP errors
    if (useCloudRunApi && !isRetry && isNetworkError(error)) {
      logIndesignFallback(route, error as Error);
      return callIndesignTemplateSpecs(args, true);
    }
    throw error;
  }
};

export const callIndesignLinerResponse = async (
  args: CallIndesignArgs,
  isRetry = false
): Promise<CallIndesignResponse<EDisplayParams>> => {
  await setCustomLDContext({
    route: 'indesign'
  });
  const useCloudRunApi = isRetry
    ? false
    : getBooleanFlag(LaunchDarklyFlags.USE_CLOUD_RUN_API, false);
  const route = IndesignRoute.liner_ad_image;

  try {
    return await callInDesignService(
      `${getIndesignServiceUrl(useCloudRunApi)}/${route}`,
      args.request,
      useCloudRunApi,
      args.options
    );
  } catch (error) {
    // Only retry network failures (e.g., "Failed to fetch"), not HTTP errors
    if (useCloudRunApi && !isRetry && isNetworkError(error)) {
      logIndesignFallback(route, error as Error);
      return callIndesignLinerResponse(args, true);
    }
    throw error;
  }
};
